// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DYNAMIC_FORM_TYPES, IProps } from './types';
import { fillFormData, getFormBlocks } from './utils';
import { ELEMENT_IDS, STYLES } from '../../../pages/Supplier/consts';
import { RenderSelectField } from '../../../pages/Supplier/manageComponents/RenderSelectField';
import { RenderTextFiled } from '../../../pages/Supplier/manageComponents/RenderTextFiled';
import Heading from '../../../generic-components/Heading';
import Button from '../../../generic-components/Button';
import { ReactComponent as ChevronIcon } from '../../../assets/svg/chevron.svg';
import { FORM_BLOCK_TYPES, FORM_INPUT_TYPES } from './consts';
import { eventBus } from '../../../services/eventBus';
import { RenderCheckbox } from '../../../pages/Supplier/manageComponents/RenderCheckbox';
import { validateFeatureFlag } from '../../../services/featureFlagService';

export const DynamicForm = ({
  formType,
  triggerFormSubmit,
  preFillData,
  isEditMode,
  triggerDelete,
  isLoading,
  preFilledValue,
  isUpdateOnlyMode,
  shouldHideFooter,
  shouldAvoidValidateAfterPreFill,
  formValidationStatus,
  onFormData,
}: IProps) => {
  const [focusInputType, setFocusInputType] = useState<string>('');
  const [activeTriggers, setActiveTriggers] = useState<{}>({});
  const formBlocks = useMemo(() => getFormBlocks(formType), [formType]);
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    trigger,
    setValue,
    getValues,
    setError,
    setFocus,
  } = useForm();

  useEffect(() => {
    const formsToAvoidInitialValidation = [DYNAMIC_FORM_TYPES.ACH_OUT_MANDATORY, DYNAMIC_FORM_TYPES.WIRE_OUT_MANDATORY];
    if (preFillData && Object.keys(preFillData).length) {
      fillFormData(preFillData, formType, setValue);
      if (!formsToAvoidInitialValidation.includes(formType)) trigger();
    }
  }, [preFillData]);

  useEffect(() => {
    if (preFilledValue?.inputId && preFilledValue.value) {
      setValue(preFilledValue.inputId, preFilledValue.value);
      if (!shouldAvoidValidateAfterPreFill) trigger();
    }
  }, [preFilledValue]);

  useEffect(() => {
    if (formValidationStatus) formValidationStatus(isValid);
    if (isValid && onFormData) onFormData(getValues());
  }, [isValid]);

  useEffect(() => {
    eventBus.on('triggerFormValidation', () => {
      trigger();
    });
  }, []);

  const onSubmit = (data: any) => {
    triggerFormSubmit(data);
  };

  const handleTrigger = (id: string) => {
    setActiveTriggers((prevState) => ({ ...prevState, [id]: !activeTriggers[id] }));
  };

  const renderTextField = (item: any) => {
    const shouldHide = item.linkedTriggerId && !activeTriggers[item.linkedTriggerId];
    return (
      <RenderTextFiled
        item={item}
        shouldHideField={shouldHide}
        getValues={getValues}
        control={control}
        setFocus={setFocus}
        focusInputType={focusInputType}
        setValue={setValue}
        errors={errors}
        trigger={trigger}
        setError={setError}
        setFocusInputType={setFocusInputType}
      />
    );
  };
  const renderSelector = (item: any) => {
    const shouldHide = item.linkedTriggerId && !activeTriggers[item.linkedTriggerId];
    return (
      <RenderSelectField
        shouldHideField={shouldHide}
        setError={setError}
        item={item}
        control={control}
        errors={errors}
        trigger={trigger}
      />
    );
  };
  const renderCheckbox = (item: any) => {
    const shouldHide = item.featureFlagId && !validateFeatureFlag(item.featureFlagId);
    return (
      <RenderCheckbox
        item={item}
        shouldHideField={shouldHide}
        control={control}
        setValue={setValue}
      />
    );
  };

  const renderTriggerTitle = (title: string, triggerId: string) => (
    <Button
      onClick={() => handleTrigger(triggerId)}
      className='w-full flex items-center justify-start'
      id={`trigger-${triggerId}`}
      childElement={
        <div className='flex items-center'>
          <Heading title={t(title)} className='font-metricR text-[16px] text-bw08' />
          <ChevronIcon
            style={{
              transform: `rotate(${activeTriggers[triggerId] ? 180 : 0}deg)`,
              transition: '0.25s ease-in',
            }}
          />
        </div>
      }
    />
  );

  // eslint-disable-next-line array-callback-return,consistent-return
  const renderFormV2 = () => formBlocks.map(item => {
    if (item.type === FORM_BLOCK_TYPES.TRIGGER) {
      if (item.shouldHideTrigger) return '';
      return renderTriggerTitle(item.triggerTitle, item.triggerId);
    }
    if (item.type === FORM_BLOCK_TYPES.SINGLE) {
      if (item.inputType === FORM_INPUT_TYPES.TEXT) return renderTextField(item);
      if (item.inputType === FORM_INPUT_TYPES.SELECT) return renderSelector(item);
      if (item.inputType === FORM_INPUT_TYPES.CHECKBOX) return renderCheckbox(item);
    } else if (item.type === FORM_BLOCK_TYPES.MULTIPLE) {
      return (
        <div className={STYLES.COMBINE_FORM_INPUT}>
          {item.children.map(childrenItem => {
            if (childrenItem.inputType === FORM_INPUT_TYPES.TEXT) return renderTextField(childrenItem);
            if (childrenItem.inputType === FORM_INPUT_TYPES.SELECT) return renderSelector(childrenItem);
            if (childrenItem.inputType === FORM_INPUT_TYPES.CHECKBOX) return renderCheckbox(childrenItem);
            return '';
          })}
        </div>
      );
    }
  });

  return (
    <form
      className={`w-full flex flex-col gap-[24px] ${shouldHideFooter ? '' : 'pb-[130px]'}`}
      id={ELEMENT_IDS.FORM}
      onSubmit={handleSubmit(onSubmit)}
    >
      {renderFormV2()}
      {!shouldHideFooter && (
        <div className='absolute z-[99] w-full left-0 bottom-0 bg-bw00 flex justify-center py-[24px] border-t border-bw03'>
          {isEditMode ? (
            <div className='flex items-center gap-[16px] w-[375px]'>
              <Button
                disable={isLoading}
                isLoading={isLoading}
                title='Delete Vendor'
                className='hover:shadow-bottomShadow bg-red01 text-red02 py-[18px] rounded-[4px] text-[16px] font-metricRMedium w-[375px]'
                id='elBtnDelete'
                onClick={triggerDelete}
              />
              <Button
                disable={!isValid || isLoading}
                isLoading={isLoading}
                title='Save'
                className={`py-[18px] rounded-[4px] text-[16px] font-metricRMedium w-[375px] text-bw00 ${isValid ? 'bg-action03 hover:bg-action04 active:bg-action04 hover:shadow-bottomShadow' : 'bg-bw05'}`}
                id='elBtnContinue'
                onClick={handleSubmit(onSubmit)}
              />
            </div>
          ) : (
            <Button
              disable={!isValid || isLoading}
              isLoading={isLoading}
              title={isUpdateOnlyMode ? 'Confirm' : 'Create'}
              className={`text-bw00 py-[18px] rounded-[4px] text-[16px] font-metricRMedium w-[375px] ${isValid ? 'hover:shadow-bottomShadow bg-action03 hover:bg-action04 active:bg-action04' : 'bg-bw05'}`}
              id='elBtnContinue'
              onClick={handleSubmit(onSubmit)}
            />
          )}
        </div>
      )}
    </form>
  );
};
